import { faInfoCircle, faLayerGroup } from "@fortawesome/pro-solid-svg-icons"
import { FilterList, Save } from "@mui/icons-material"
import { Box, Button, Grid, IconButton, Skeleton, TableHead, Tooltip, Typography, useMediaQuery } from "@mui/material"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableRow from "@mui/material/TableRow"
import { useApi } from "auth/useApi3"
import React, { useContext, useEffect, useMemo, useState } from "react"

import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  SortDirection,
  SortingState,
  useReactTable,
  Row
} from "@tanstack/react-table"
import { IApiFunc, IApiResult } from "auth/interface/api.interface"
import { AppContext } from "components/AppContextProvider"
import DefaultButton from "components/Button"
import NoResultFound from "components/Dialogs/NoResultFound"
import theme from "css/theme/light"
import { apiEndpoints } from "generated/apiEndpoints"
import { useHistory } from "react-router-dom"
import { StyledBox, StyledFilterContainer, StyledTable, StyledTableFooter } from "./ClpTable.styled"
import { ClpTableEnum } from "./ClpTableEnum"
import { DefaultColumnFilter, GlobalFilter } from "./ClpTableFilterTypes"
import ClpTableRow from "./components/ClpTableRow"
import ExportToExcel from "./components/ExportToExcel"
import { StyledFilterGrid } from "./components/FilterGrid.styled"
import MoreFilters from "./components/MoreFilters"
import Pagination from "./components/Pagination"
import PrintTableToPdt from "./components/PrintToPdf"
import RadioButtons from "./components/RadioButtons"
import SelectedFilters from "./components/SelectedFilters"
import TableHeader from "./components/TableHeader"
import { ConvertedTable, TableLoadingStateEnum, TableProps } from "./tableProps.interface"
import { useTranslation } from "react-i18next"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { convertTableForExport } from "./components/helpers/convertTableForExport"

interface UpsertFiltersDto {
  clpTableId: ClpTableEnum
  columnFilters: ColumnFiltersState
  globalFilter: string
}

const emptyState = {
  pageSize: 25,
  pageIndex: 0,
  expanded: {},
  sortBy: [],
  filters: [],
  hiddenColumns: []
}

const ClpTable = <T extends object>({
  rows,
  columns,
  subColumns,
  initialFilters,
  loadingState,
  hideSearch,
  hideFilter,
  onRowClick,
  print,
  footer,
  tableId,
  saveFilterTooltipText,
  noRowsErrorText,
  defaultSorting
}: TableProps<T>) => {
  const appContext = useContext(AppContext)
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(initialFilters ? initialFilters : [])
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25
  })
  const [sort, setSort] = React.useState<SortDirection | []>([])
  const [filtersOpen, expandFilters] = useState(false)
  const [globalFilter, setGlobalFilter] = React.useState("")
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"))
  const [saveFiltersApiResult, saveFiltersApi] = useApi() as [IApiResult, IApiFunc]
  const [getSavedFiltersApiResult, getSavedFiltersApi] = useApi() as [IApiResult, IApiFunc]

  function showAllFilterOptionsFix() {
    if (tableId == null || !Array.isArray(rows)) return

    for (const column of columns.filter((c) => c.filterFn != null)) {
      const header = typeof column.header === "string" ? column.header : undefined
      const accessorKey = (column as { accessorKey?: string }).accessorKey

      if (!header || !accessorKey || !column.meta) continue
      if (column.meta.filterOptions != null) continue

      const filterOptionNames = rows.map((row) => getValueByAccessor(row, accessorKey))

      const filterOptions = Array.from(
        new Set(filterOptionNames.filter((val): val is string => typeof val === "string"))
      ).map((val) => ({
        name: val,
        value: val
      }))

      column.meta.filterHeader = header
      column.meta.filterOptions = filterOptions
    }
  }

  useEffect(() => {
    showAllFilterOptionsFix()
  })

  function getValueByAccessor<T>(row: T, accessor: string): any {
    return accessor.split(".").reduce((obj, key) => obj?.[key], row as any)
  }

  function saveFilters() {
    if (tableId == null) return

    for (const filter of columnFilters) {
      //value Type correction and validation
      const { id, value } = filter

      if (typeof value === "string") {
        filter.value = [value]
      }

      if (Array.isArray(value) && value.every((v) => typeof v === "string")) {
        continue
      } else {
        console.error(`Filter "${id}" has invalid type. Expected string[] or string, got:`, value)
      }
    }

    const body: UpsertFiltersDto = {
      clpTableId: tableId,
      columnFilters: columnFilters.map(column => ({
        ...column,
        value: Array.isArray(column.value) ? column.value.map(val => String(val)) : column.value
      })),
      globalFilter: globalFilter
    }

    saveFiltersApi.put(apiEndpoints.clptableuserfilter.upsert, body)
  }

  function fetchSavedFilters() {
    if (tableId == null) return

    const url = `${apiEndpoints.clptableuserfilter.get}?clpTableId=${tableId}`
    getSavedFiltersApi.get(url)
  }

  useEffect(() => {
    fetchSavedFilters()
  }, [])

  useEffect(() => {
    if (getSavedFiltersApiResult?.status === 2 && getSavedFiltersApiResult?.data) {
      const savedFilters = getSavedFiltersApiResult.data
      const columnFilters = Array.isArray(savedFilters?.columnFilters)
        ? savedFilters.columnFilters.map((column: any) => ({
          ...column,
          value: Array.isArray(column?.value)
            ? column.value.map((val: any) => {
              return Number(val) || val
            })
            : column.value
        }))
        : savedFilters
      setColumnFilters(columnFilters || [])
      setGlobalFilter(savedFilters.globalFilter || "")
    }
  }, [getSavedFiltersApiResult])

  useEffect(() => {
    const initialValues =
      JSON.stringify(appContext.tableState.columns) ===
        JSON.stringify(columns.map((x: ColumnDef<T>) => x.header))
        ? appContext.tableState.state
        : emptyState

    setPagination(() => ({
      pageSize: initialValues?.pagination?.pageSize ? initialValues.pagination.pageSize : 25,
      pageIndex: initialValues?.pagination?.pageIndex ? initialValues.pagination.pageIndex : 0
    }))
    setSort(() => (initialValues?.sortBy ? initialValues.sortBy : []))
  }, [appContext.tableState.columns, appContext.tableState.state, columns])

  const tableData = useMemo(
    () =>
      loadingState === TableLoadingStateEnum.Loading
        ? Array(25).fill({})
        : loadingState === TableLoadingStateEnum.Success && rows
          ? rows
          : [],
    [loadingState, rows]
  )

  const tableColumns = React.useMemo(
    () =>
      loadingState === TableLoadingStateEnum.Loading
        ? columns.map((column: ColumnDef<T>) => ({
          ...column,
          cell: () => <Skeleton />
        }))
        : loadingState === TableLoadingStateEnum.Success
          ? columns
          : [],
    [loadingState, columns]
  )

  const table = useReactTable({
    defaultColumn: { meta: { filter: DefaultColumnFilter } },
    columns: tableColumns,
    data: tableData,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    onPaginationChange: setPagination,
    // debugTable: true,
    // debugHeaders: true,
    // debugColumns: false,
    state: {
      columnFilters: columnFilters,
      pagination,
      globalFilter
    },
    initialState: {
      pagination: {
        pageSize: pagination.pageSize
      },
      sorting: defaultSorting ?? [],
    }
  })

  const {
    getHeaderGroups,
    getRowModel,
    nextPage,
    previousPage,
    setPageSize,
    getCanNextPage,
    getCanPreviousPage,
    setPageIndex,
    getPageCount,
    getRowCount,
    getState
  } = table
  const history = useHistory()
  const { t } = useTranslation()

  useEffect(() => {
    const handleLeave = () => { }
    const unlisten = history.listen((location, action) => {
      appContext.setTableState(() => {
        return {
          state: getState(),
          columns: columns.map((x) => x.header)
        }
      })
      if (action === "POP") {
        handleLeave()
      }
    })

    return () => {
      unlisten()
      handleLeave()
    }
  }, [history, getState()])
  const renderNoRows = () => {
    return <NoResultFound icon={faLayerGroup}
      message={noRowsErrorText || t("global.table.no_results_found", "No results found")}
      backgroundColor={(theme.palette as any).primary[300]}
      color={(theme.palette as any).primary[800]} size="lg" />
  }

  const renderFooter = () => {
    return (
      <StyledTableFooter>
        {footer && footer.length > 0 && (
          <TableRow>
            <TableCell>{t("global.table.total", "Total")}:</TableCell>
            {footer.map((item: any) => (
              <TableCell key={item}>
                {item.value > 0 && parseFloat(item.value).toFixed(2).replace(".", ",")}
              </TableCell>
            ))}
          </TableRow>
        )}
      </StyledTableFooter>
    )
  }

  const renderPagination = () => {
    return (
      <>
        <Pagination
          pageIndex={getState().pagination.pageIndex + 1}
          gotoPage={setPageIndex}
          previousPage={previousPage}
          canPreviousPage={getCanPreviousPage}
          nextPage={nextPage}
          canNextPage={getCanNextPage}
          pageCount={getPageCount}
        />
      </>
    )
  }

  let convertedTable = convertTableForExport(table.getFilteredRowModel().rows, columns, t, tableId, footer)

  return (
    <>
      <StyledFilterContainer>
        <Box>
          {rows?.length > 0 && !hideSearch && <Grid display="flex" item>
            <Typography variant="h6" mb={1}>
              {t("global.table.filter", "Filter")}:
            </Typography>
          </Grid>}
          <Grid container direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Grid xs={isPhone && 12} item>
              <StyledFilterGrid gap={2}>
                {!hideSearch && setGlobalFilter && (
                  <GlobalFilter initialValue={globalFilter} setGlobalFilter={setGlobalFilter} />
                )}
                {getHeaderGroups()?.map((headerGroup) =>
                  headerGroup.headers
                    .filter(
                      (x) =>
                        !x.column.columnDef.meta?.hiddenFilter &&
                        x.column.columnDef.meta?.mainFilter &&
                        x.column.columnDef.header
                    )
                    .map((header, index) => {
                      return (
                        header.column.getCanFilter() && (
                          <Grid item key={index}>
                            {flexRender(header.column.columnDef?.meta?.filter ?? DefaultColumnFilter, {
                              column: header.column
                            })}
                          </Grid>
                        )
                      )
                    })
                )}
              </StyledFilterGrid>
            </Grid>
            <Grid item>
              <Grid container direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                {!hideFilter && (
                  <Grid item>
                    <Button
                      color="primary"
                      startIcon={<FilterList />}
                      onClick={() => expandFilters(!filtersOpen)}>
                      {filtersOpen ? t("global.table.hide_filter", "Hide filter") : t("global.table.show_all_filter", "Show all filters")}
                    </Button>
                  </Grid>
                )}
                {tableId != null && (
                  <Grid item>
                    <DefaultButton
                      variant="text"
                      startIcon={<Save />}
                      result={saveFiltersApiResult}
                      onClick={() => saveFilters()}>
                      {t("global.table.save_filter", "Save filter")}
                    </DefaultButton>
                    {saveFilterTooltipText && <Tooltip title={saveFilterTooltipText}>
                      <IconButton size="small" sx={{ color: (theme.palette as any).primary[700] }}>
                        <FontAwesomeIcon icon={faInfoCircle} />
                      </IconButton>
                    </Tooltip>}
                  </Grid>
                )}
                <Grid item>
                  <ExportToExcel convertedTable={convertedTable} />
                  {print && <PrintTableToPdt columns={columns} rows={table.getFilteredRowModel().rows} />}
                </Grid>
                <Grid item>
                  <RadioButtons pageSize={getState().pagination.pageSize} setPageSize={setPageSize} />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
        <Grid container direction="column" justifyContent="space-between">
          {filtersOpen && (
            <Grid item>
              <MoreFilters rows={getRowModel()?.rows} headerGroups={getHeaderGroups()} />
            </Grid>
          )}
          <Grid item>
            <SelectedFilters table={table} setFiltering={setColumnFilters} />
          </Grid>
        </Grid>
      </StyledFilterContainer>
      <Box marginX={2} mb={1} display="flex" justifyContent="space-between" alignItems="center">
        {loadingState === TableLoadingStateEnum.Success &&
          (rows && rows.length > 0 &&
            <Grid container direction="row" alignItems="center" justifyContent="space-between" gap={1}>
              <Grid>
                <Typography >{t("global.table.total", "Total")}: {getRowCount()} {t("global.rows", "Rows")}</Typography>
              </Grid>
            </Grid>
          )}
        <Grid container direction="row" alignItems="center" justifyContent="flex-end" spacing={2}>
          <Grid item>{renderPagination()} </Grid>
        </Grid>
      </Box>
      <StyledBox>
        <StyledTable size="small">
          {getHeaderGroups().map((headerGroup) => (
            <TableHead key={headerGroup.id} sx={{ display: { xs: "none", md: "table-header-group" } }}>
              <TableRow>
                {subColumns && subColumns.length > 0 && <TableCell key="subColumnHeader" />}
                {headerGroup.headers.map((header, index) => {
                  return header.isPlaceholder ? null : <TableHeader header={header} />
                })}
              </TableRow>
            </TableHead>
          ))}
          <TableBody>
            {getRowModel()?.rows?.map((row, i) => {
              return (
                <ClpTableRow
                  columns={columns}
                  key={i}
                  row={row}
                  subColumns={subColumns}
                  onRowClick={onRowClick}
                />
              )
            })}
          </TableBody>

          {renderFooter()}
        </StyledTable>
        {loadingState === TableLoadingStateEnum.Success &&
          (rows && rows.length > 0 ? (
            <Grid container direction="row" alignItems="center" justifyContent="space-between" gap={1}>
              <Grid pl={2} py={1}>
                <Typography >{t("global.table.total", "Total")}: {getRowCount()} {t("global.rows", "Rows")}</Typography>
              </Grid>
            </Grid>
          ) : (
            renderNoRows()
          ))}
      </StyledBox>
    </>
  )
}

export default ClpTable
